import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import ActionButton from "../buttons/Action";
import message from "../../helper/message";
import QRCodeLib from "qrcode";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import {
  PageWrapper,
  Card,
  SetupWrapper,
  FormTitle,
  Field,
  Label,
  Input,
  Buttons,
} from "./MfaSharedLayout";

export default function MfaSetup({
  secret,
  otpauthUrl,
  onConfirm,
  onCancel,
  isLoading,
}) {
  const [code, setCode] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    QRCodeLib.toDataURL(otpauthUrl, { width: 260, margin: 1 })
      .then((url) => mounted && setQrDataUrl(url))
      .catch((err) => {
        console.error("Erro ao gerar QR:", err);
        message.error("Não foi possível gerar o QR. Use o secret manualmente.");
      });
    return () => {
      mounted = false;
    };
  }, [otpauthUrl]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!code) {
      message.error("Informe o código do autenticador para confirmar.");
      return;
    }
    onConfirm(code);
  };

  return (
    <PageWrapper>
      <Card>
        <SetupWrapper>
          <FormTitle>Ativar MFA (TOTP)</FormTitle>
          <FormSubtitle>
            1. Escaneie o QR Code com seu aplicativo autenticador (Google Authenticator, Authy, etc.)<br/>
            2. Aguarde o código aparecer no app (ele muda a cada 30 segundos)<br/>
            3. Digite o código de 6 dígitos abaixo
          </FormSubtitle>

          <QRCodeWrapper>
            <QrFrame>
              {qrDataUrl ? (
                <img src={qrDataUrl} alt="QR Code MFA" />
              ) : (
                <FallbackText>Gerando QR...</FallbackText>
              )}
            </QrFrame>
            <SecretText>{secret}</SecretText>
            <SmallHint>
              Se não conseguir ler o QR, cadastre o código acima manualmente no app.
            </SmallHint>
          </QRCodeWrapper>

          <form onSubmit={handleSubmit}>
            <Field>
              <Label htmlFor="mfa-setup-code">Código do autenticador</Label>
              <Input
                ref={inputRef}
                id="mfa-setup-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={10}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                required
              />
            </Field>
            <Buttons>
              <ActionButton
                icon={CheckCircleIcon}
                text={isLoading ? "Validando..." : "Confirmar MFA"}
                type="submit"
                disabled={isLoading || !code}
                variant="primary"
                loading={isLoading}
              />
              <ActionButton
                icon={CancelIcon}
                text="Cancelar"
                type="button"
                onClick={onCancel}
                disabled={isLoading}
                variant="secondary"
              />
            </Buttons>
          </form>
        </SetupWrapper>
      </Card>
    </PageWrapper>
  );
}

const FormSubtitle = styled.p`
  margin: 0 0 24px 0;
  color: #64748b;
  line-height: 1.5;
  text-align: center;
  font-size: 14px;
  font-family: 'Mulish', sans-serif;
`;

const QRCodeWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  margin: 0 0 24px 0;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
`;

const SecretText = styled.code`
  font-size: 12px;
  color: #1e293b;
  background: #ffffff;
  padding: 10px 16px;
  border-radius: 6px;
  word-break: break-all;
  font-family: 'Courier New', monospace;
  letter-spacing: 1.5px;
  border: 1px solid #e2e8f0;
  font-weight: 600;
`;

const SmallHint = styled.span`
  font-size: 12px;
  color: #94a3b8;
  text-align: center;
  max-width: 320px;
  line-height: 1.4;
  font-family: 'Mulish', sans-serif;
`;

const QrFrame = styled.div`
  border: 8px solid #ffffff;
  border-radius: 12px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  img {
    display: block;
    width: 200px;
    height: 200px;
    object-fit: contain;
  }
`;

const FallbackText = styled.span`
  display: block;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  font-size: 14px;
  font-family: 'Mulish', sans-serif;
`;
